import { create } from 'zustand';
import { i18n } from '../i18n';
import {
  createTextSnippet,
  deleteTextSnippet,
  getTextSnippets,
  setTextSnippetDefault,
  updateTextSnippet,
} from '../api/textSnippets';
import type {
  CreateTextSnippetRequest,
  GetTextSnippetsParams,
  TextSnippetCategory,
  TextSnippetDto,
  UpdateTextSnippetRequest,
} from '../types/textSnippets';
import { useToastStore } from './useToastStore';

interface TextSnippetState {
  items: TextSnippetDto[];
  loading: boolean;
  saving: boolean;
  error: string | null;
  lastLoadedAt: number | null;

  fetchSnippets: (params?: GetTextSnippetsParams) => Promise<void>;
  createSnippet: (data: CreateTextSnippetRequest) => Promise<TextSnippetDto | null>;
  updateSnippet: (id: string, data: UpdateTextSnippetRequest) => Promise<void>;
  deleteSnippet: (id: string) => Promise<void>;
  setDefault: (id: string) => Promise<void>;

  getByCategory: (category: TextSnippetCategory, onlyActive?: boolean) => TextSnippetDto[];
  getDefaultByCategory: (category: TextSnippetCategory) => TextSnippetDto | undefined;
}

const t = (key: string, options?: Record<string, unknown>) =>
  i18n.t(key, { ns: 'textSnippets', ...options }) as string;

export const useTextSnippetStore = create<TextSnippetState>((set, get) => ({
  items: [],
  loading: false,
  saving: false,
  error: null,
  lastLoadedAt: null,

  fetchSnippets: async (params) => {
    set({ loading: true, error: null });
    try {
      const data = await getTextSnippets(params);
      const sorted = [...(data || [])].sort((a, b) => a.sortOrder - b.sortOrder);
      set({ items: sorted, loading: false, lastLoadedAt: Date.now() });
    } catch (error) {
      console.error('[TextSnippetStore] fetch failed', error);
      set({
        error: t('errors.fetchFailed'),
        loading: false,
      });
    }
  },

  createSnippet: async (data) => {
    set({ saving: true });
    try {
      const created = await createTextSnippet(data);
      set((state) => {
        let items = [...state.items, created];
        if (created.isDefault) {
          items = items.map((s) =>
            s.category === created.category && s.id !== created.id
              ? { ...s, isDefault: false }
              : s
          );
        }
        items.sort((a, b) => a.sortOrder - b.sortOrder);
        return { items, saving: false };
      });
      useToastStore.getState().addToast(t('messages.created'), 'success');
      return created;
    } catch (error) {
      console.error('[TextSnippetStore] create failed', error);
      set({ saving: false });
      useToastStore.getState().addToast(t('errors.saveFailed'), 'error');
      return null;
    }
  },

  updateSnippet: async (id, data) => {
    set({ saving: true });
    try {
      const updated = await updateTextSnippet(id, data);
      set((state) => ({
        items: state.items
          .map((s) => (s.id === id ? updated : s))
          .sort((a, b) => a.sortOrder - b.sortOrder),
        saving: false,
      }));
      useToastStore.getState().addToast(t('messages.updated'), 'success');
    } catch (error) {
      console.error('[TextSnippetStore] update failed', error);
      set({ saving: false });
      useToastStore.getState().addToast(t('errors.saveFailed'), 'error');
      throw error;
    }
  },

  deleteSnippet: async (id) => {
    set({ saving: true });
    try {
      await deleteTextSnippet(id);
      set((state) => ({
        items: state.items.filter((s) => s.id !== id),
        saving: false,
      }));
      useToastStore.getState().addToast(t('messages.deleted'), 'success');
    } catch (error) {
      console.error('[TextSnippetStore] delete failed', error);
      set({ saving: false });
      useToastStore.getState().addToast(t('errors.deleteFailed'), 'error');
      throw error;
    }
  },

  setDefault: async (id) => {
    set({ saving: true });
    try {
      const updated = await setTextSnippetDefault(id);
      set((state) => ({
        items: state.items.map((s) => {
          if (s.id === updated.id) return updated;
          if (s.category === updated.category) return { ...s, isDefault: false };
          return s;
        }),
        saving: false,
      }));
      useToastStore.getState().addToast(t('messages.defaultSet'), 'success');
    } catch (error) {
      console.error('[TextSnippetStore] setDefault failed', error);
      set({ saving: false });
      useToastStore.getState().addToast(t('errors.setDefaultFailed'), 'error');
      throw error;
    }
  },

  getByCategory: (category, onlyActive = false) =>
    get()
      .items.filter((s) => s.category === category && (!onlyActive || s.isActive))
      .sort((a, b) => a.sortOrder - b.sortOrder),

  getDefaultByCategory: (category) =>
    get().items.find((s) => s.category === category && s.isDefault && s.isActive),
}));
